import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ReferenceAccount, ReferenceSite, ReferenceSnapshotData } from '../types';
import {
  REFERENCE_SNAPSHOT_SHAREPOINT_URL,
  findAccount,
  findSite,
  loadReferenceSnapshot,
  searchAccounts,
  searchSites,
} from '../utils/referenceSnapshot';

interface ReferenceDataPickerProps {
  selectedAccountId?: string;
  selectedSiteId?: string;
  onSelectAccount: (account: ReferenceAccount) => void;
  onSelectSite: (site: ReferenceSite) => void;
  onClearAccount: () => void;
  onClearSite: () => void;
  /** Hide the site picker (e.g. if a form only needs the customer). */
  showSitePicker?: boolean;
}

function formatSnapshotDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }
  return date.toLocaleDateString('fi-FI');
}

interface ComboboxOption {
  id: string;
  label: string;
  detail: string;
}

interface ReferenceComboboxProps {
  label: string;
  placeholder: string;
  noResultsText: string;
  selected: ComboboxOption | null;
  options: (query: string) => ComboboxOption[];
  onSelect: (id: string) => void;
  onClear: () => void;
  testId: string;
}

const ReferenceCombobox = ({
  label,
  placeholder,
  noResultsText,
  selected,
  options,
  onSelect,
  onClear,
  testId,
}: ReferenceComboboxProps) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, []);

  const results = useMemo(() => (open ? options(query) : []), [open, options, query]);

  if (selected) {
    return (
      <div className="form-group reference-picker-group" data-testid={testId}>
        <label>{label}</label>
        <div className="reference-picker-selected">
          <span className="reference-picker-selected-label">
            ✓ {selected.label}
            <span className="reference-picker-selected-id"> {selected.id}</span>
          </span>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={onClear}
            data-testid={`${testId}-clear`}
          >
            ✕
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="form-group reference-picker-group" ref={containerRef} data-testid={testId}>
      <label>{label}</label>
      <input
        type="text"
        className="form-input"
        value={query}
        placeholder={placeholder}
        onFocus={() => setOpen(true)}
        onChange={(event) => {
          setQuery(event.target.value);
          setOpen(true);
        }}
        data-testid={`${testId}-input`}
      />
      {open && (
        <ul className="reference-picker-options" data-testid={`${testId}-options`}>
          {results.length === 0 && (
            <li className="reference-picker-option reference-picker-option-empty">
              {noResultsText}
            </li>
          )}
          {results.map((option) => (
            <li key={option.id}>
              <button
                type="button"
                className="reference-picker-option"
                onClick={() => {
                  onSelect(option.id);
                  setQuery('');
                  setOpen(false);
                }}
              >
                <span className="reference-picker-option-label">{option.label}</span>
                <span className="reference-picker-option-detail">{option.detail}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/**
 * Master reference data selectors (F2): customer + site are picked from the
 * imported Halton Data snapshot and stamp IA#####/SL-##### onto documents.
 * Free-text entry in the underlying form remains available as fallback.
 */
export const ReferenceDataPicker = ({
  selectedAccountId,
  selectedSiteId,
  onSelectAccount,
  onSelectSite,
  onClearAccount,
  onClearSite,
  showSitePicker = true,
}: ReferenceDataPickerProps) => {
  const { t } = useTranslation();
  const [snapshot, setSnapshot] = useState<ReferenceSnapshotData | null>(() =>
    loadReferenceSnapshot()
  );

  // Re-read when another part of the app (Settings import) updates the snapshot.
  useEffect(() => {
    const handleFocus = () => setSnapshot(loadReferenceSnapshot());
    window.addEventListener('halton-reference-snapshot-updated', handleFocus);
    return () => window.removeEventListener('halton-reference-snapshot-updated', handleFocus);
  }, []);

  if (!snapshot) {
    return (
      <div className="reference-picker reference-picker-empty" data-testid="reference-picker-empty">
        <span>{t('referenceData.noSnapshot')}</span>{' '}
        <a href={REFERENCE_SNAPSHOT_SHAREPOINT_URL} target="_blank" rel="noreferrer">
          {t('referenceData.downloadLink')}
        </a>
      </div>
    );
  }

  const selectedAccount = selectedAccountId ? findAccount(snapshot, selectedAccountId) : undefined;
  const selectedSite = selectedSiteId ? findSite(snapshot, selectedSiteId) : undefined;

  return (
    <div className="reference-picker" data-testid="reference-picker">
      <div className="form-row form-row-2">
        <ReferenceCombobox
          label={t('referenceData.accountLabel')}
          placeholder={t('referenceData.accountPlaceholder')}
          noResultsText={t('referenceData.noResults')}
          testId="reference-account-picker"
          selected={
            selectedAccount
              ? {
                  id: selectedAccount.internalAccountId,
                  label: selectedAccount.accountName,
                  detail: selectedAccount.vatId,
                }
              : null
          }
          options={(query) =>
            searchAccounts(snapshot, query).map((account) => ({
              id: account.internalAccountId,
              label: account.accountName,
              detail: [account.internalAccountId, account.vatId].filter(Boolean).join(' · '),
            }))
          }
          onSelect={(id) => {
            const account = findAccount(snapshot, id);
            if (account) {
              onSelectAccount(account);
            }
          }}
          onClear={onClearAccount}
        />
        {showSitePicker && (
          <ReferenceCombobox
            label={t('referenceData.siteLabel')}
            placeholder={t('referenceData.sitePlaceholder')}
            noResultsText={t('referenceData.noResults')}
            testId="reference-site-picker"
            selected={
              selectedSite
                ? {
                    id: selectedSite.siteLocationId,
                    label: selectedSite.customerSite,
                    detail: selectedSite.city,
                  }
                : null
            }
            options={(query) =>
              searchSites(snapshot, selectedAccountId, query).map((site) => ({
                id: site.siteLocationId,
                label: site.customerSite,
                detail: [site.siteLocationId, site.city].filter(Boolean).join(' · '),
              }))
            }
            onSelect={(id) => {
              const site = findSite(snapshot, id);
              if (site) {
                onSelectSite(site);
              }
            }}
            onClear={onClearSite}
          />
        )}
      </div>
      <small className="form-hint reference-picker-status" data-testid="reference-picker-status">
        {t('referenceData.updatedAt', { date: formatSnapshotDate(snapshot.generatedAt) })}{' '}
        <a href={REFERENCE_SNAPSHOT_SHAREPOINT_URL} target="_blank" rel="noreferrer">
          {t('referenceData.downloadLink')}
        </a>
      </small>
    </div>
  );
};

export default ReferenceDataPicker;
